import { logger } from '@moonpig/web-core-monitoring'
import { AuthFetch } from '@moonpig/web-core-auth'
import {
  AuthenticationOidcServiceType,
  OidcAccountLinkingRequiredType,
  OidcLoginInput,
  OidcLoginResult,
  OidcLoginAccountLinkingRequiredResult,
  OidcLoginStatus,
  OidcProvidersResult,
  OidcStartInput,
  OidcStartResult,
  oidcErrorLogIdentifier,
} from './types'
import { trackSocialException } from './tracking'

export const authenticationOidcService = (
  authFetch: AuthFetch,
): AuthenticationOidcServiceType => {
  const getOidcProviders = async (): Promise<OidcProvidersResult> => {
    const emptyResult: OidcProvidersResult = { providers: [] }

    try {
      const response = await authFetch({
        path: '/oidc/providers',
        ...{ method: 'GET' },
      })

      switch (response.status) {
        case 200: {
          const apiResponse: OidcProvidersResult = await response.json()

          return {
            providers: apiResponse.providers,
          }
        }
        default:
          logger.fixToday(
            `${oidcErrorLogIdentifier} Failed to get oidc providers`,
            {
              responseStatusCode: response.status,
              response: await response.text(),
            },
          )
          return emptyResult
      }
    } catch (error) {
      logger.fixToday(
        `${oidcErrorLogIdentifier} Failed to get oidc providers (exception)`,
        undefined,
        error,
      )
      return emptyResult
    }
  }

  const start = async (
    input: OidcStartInput,
  ): Promise<OidcStartResult | null> => {
    try {
      const response = await authFetch({
        path: '/oidc/start',
        body: JSON.stringify(input),
      })

      switch (response.status) {
        case 200: {
          const apiResponse: OidcStartResult = await response.json()
          return apiResponse
        }
        default: {
          const errorMessage = await response.text()
          logger.fixToday(`${oidcErrorLogIdentifier} Failed to do oidc start`, {
            responseStatusCode: response.status,
            response: errorMessage,
          })
          trackSocialException({
            label: input.provider,
            errorCode: response.status,
            errorMessage,
          })
          return null
        }
      }
    } catch (error) {
      logger.fixToday(
        `${oidcErrorLogIdentifier} Failed to do oidc start (exception)`,
        undefined,
        error,
      )
      trackSocialException({
        label: input.provider,
        errorMessage: 'oidc start exception',
      })
      return null
    }
  }

  const login = async (
    input: OidcLoginInput,
  ): Promise<OidcLoginResult | OidcLoginAccountLinkingRequiredResult> => {
    try {
      const response = await authFetch({
        path: '/oidc/login/code',
        body: JSON.stringify({ upstreamParameters: input.upstreamParameters }),
      })

      switch (response.status) {
        case 200:
          return { status: OidcLoginStatus.AUTHENTICATED }
        case 201:
          return { status: OidcLoginStatus.CREATED }
        case 422: {
          const apiResponse: OidcAccountLinkingRequiredType =
            await response.json()
          const { data } = apiResponse
          return {
            ...data,
            status: OidcLoginStatus.LINKING_REQUIRED,
          }
        }
        default: {
          const errorMessage = await response.text()
          logger.fixToday(`${oidcErrorLogIdentifier} Failed to do oidc login`, {
            responseStatusCode: response.status,
            response: errorMessage,
          })
          if (input.provider) {
            trackSocialException({
              label: input.provider,
              errorCode: response.status,
              errorMessage,
            })
          }
          return { status: OidcLoginStatus.ERROR }
        }
      }
    } catch (error) {
      logger.fixToday(
        `${oidcErrorLogIdentifier} Failed to do oidc login (exception)`,
        undefined,
        error,
      )
      if (input.provider) {
        trackSocialException({
          label: input.provider,
          errorMessage: 'unexpected login/code error',
        })
      }
      return { status: OidcLoginStatus.ERROR }
    }
  }

  return {
    getOidcProviders,
    start,
    login,
  }
}
